import {
  deserializeConstraint,
  HotelCodeConstraint,
  PolygonIdConstraint,
} from '@sembo-private/constraint-builder';
import { cloneDeep } from 'lodash';

import type { HotelIngredient } from '@apps/main/modules/recipe/ingredients';

import { getHotelConstraintQL } from '@libs/search-service-smart-search';
import type {
  HotelConstraintQL,
  HotelIngredientAdapter,
} from '@libs/trip-planner-interfaces';
import {
  AccommodationRule,
  IngredientType,
  AmenitiesFilter,
  ExperienceFilter,
} from '@libs/trip-planner-interfaces';

import { adaptActivityIngredientToItinerarySearchCriteria } from './activityIngredientItinerarySearchAdapter';

export function adaptHotelIngredientToItinerarySearchCriteria(
  ingredient: HotelIngredient
): HotelIngredientAdapter {
  const constraint = getHotelIngredientAdapterConstraint(ingredient);
  const hotel: HotelIngredientAdapter = {
    ingredientType: IngredientType.Hotel,
    ingredientId: ingredient.ingredientId,
    numberOfNights: ingredient.numberOfNights,
    constraint,
    heartedHotels: ingredient.heartedHotels,
    subIngredients: ingredient.subIngredients.map(
      adaptActivityIngredientToItinerarySearchCriteria
    ),
    suggestedBy: ingredient.availabilityOrder,
  };

  return cloneDeep(hotel);
}

export function getHotelIngredientAdapterConstraint(
  ingredient: HotelIngredient
): HotelConstraintQL {
  const constraint = deserializeConstraint(ingredient.searchText);

  if (ingredient.heartedHotelCodes.length) {
    constraint.selection = new HotelCodeConstraint(
      ...ingredient.heartedHotelCodes
    );
    const maxNumberOfHotels = 200; // NOTE: needs to be 200, value less than that doesn't return the suggested hotel alternatives
    constraint.maxNumberOfHotels = maxNumberOfHotels;

    const hotelConstraint = getHotelConstraintQL({
      ...constraint,
      selectedHotelTypes: ingredient.getSelectedAccommodationTypes(),
      selectedBoardBasis: ingredient.filters.boardBasis,
    });
    hotelConstraint.freeCancellation =
      ingredient.filters.selectedHotelRules.includes(
        AccommodationRule.FreeCancellation
      );
    hotelConstraint.destination =
      ingredient.searchText || ingredient.displayText;

    return hotelConstraint;
  }

  constraint.starRating = ingredient.filters.starRating;

  constraint.onlyHotelsWithPool =
    ingredient.filters.selectedAmenitiesFilters.includes(AmenitiesFilter.Pool);
  constraint.onlyHotelsWithParking =
    ingredient.filters.selectedAmenitiesFilters.includes(
      AmenitiesFilter.Parking
    );
  constraint.onlyHotelsWithSpa =
    ingredient.filters.selectedAmenitiesFilters.includes(AmenitiesFilter.Spa);
  constraint.onlyHotelsWithGym =
    ingredient.filters.selectedAmenitiesFilters.includes(AmenitiesFilter.Gym);
  constraint.onlyHotelsWithWifi =
    ingredient.filters.selectedAmenitiesFilters.includes(AmenitiesFilter.Wifi);
  constraint.onlyHotelsWithAir =
    ingredient.filters.selectedAmenitiesFilters.includes(
      AmenitiesFilter.AirConditioned
    );
  constraint.onlyHotelsWithVehicleChargingStation =
    ingredient.filters.selectedAmenitiesFilters.includes(
      AmenitiesFilter.VehicleChargingStation
    );
  constraint.onlyHotelsWithPetsAllowed =
    ingredient.filters.selectedExperienceFilters.includes(
      ExperienceFilter.PetsAllowed
    );
  constraint.onlyHotelsWithBusinessProperty =
    ingredient.filters.selectedExperienceFilters.includes(
      ExperienceFilter.Business
    );
  constraint.onlyHotelsWithFamilyProperty =
    ingredient.filters.selectedExperienceFilters.includes(
      ExperienceFilter.Family
    );

  constraint.onlyHotelsWithFreeCancellation =
    ingredient.filters.selectedHotelRules.includes(
      AccommodationRule.FreeCancellation
    );

  constraint.maxNumberOfHotels = 200;

  if (ingredient.filters.filterText) {
    constraint.hotelName = ingredient.filters.filterText;
  }

  if (ingredient.polygonId) {
    constraint.selection = new PolygonIdConstraint(ingredient.polygonId);
  }

  const hotelConstraint = getHotelConstraintQL({
    ...constraint,
    selectedHotelTypes: ingredient.getSelectedAccommodationTypes(),
    selectedBoardBasis: ingredient.filters.boardBasis,
  });

  if (ingredient.displayText) {
    hotelConstraint.destination =
      ingredient.searchText || ingredient.displayText;
  }
  hotelConstraint.freeCancellation =
    ingredient.filters.selectedHotelRules.includes(
      AccommodationRule.FreeCancellation
    );

  return hotelConstraint;
}
